import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/useToast';
import { nieuweId } from '@/lib/melden/id';
import { api, formatApiError } from '@/lib/api';
import { bestandNaarBijlage } from '@/lib/melden/beeld';
import type { MeldBericht, MeldBijlage } from '@/lib/melden/types';

const KNOP =
  'h-8 rounded-sm border border-border bg-bg-secondary px-3 text-[13px] font-medium text-text-primary ' +
  'hover:border-border-strong disabled:opacity-60';
const VELD =
  'mt-1 block w-full rounded-sm border border-border bg-bg-primary px-sm py-1.5 text-[13px] text-text-primary ' +
  'focus:border-border-strong focus:outline-hidden';

/** K3: answer the AI's question in the app -- one of its choices, or own words with an optional image. */
export function AntwoordFormulier({ externalId, vraag }: { externalId: string; vraag: MeldBericht }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tekst, setTekst] = useState('');
  const [beeld, setBeeld] = useState<MeldBijlage | null>(null);
  const [klaar, setKlaar] = useState(false);

  const stuur = useMutation({
    mutationFn: (inhoud: string) =>
      api.meldingen.bericht(externalId, {
        external_id: nieuweId(), soort: 'antwoord', tekst: inhoud, bijlagen: beeld ? [beeld] : [],
      }),
    onSuccess: () => {
      setKlaar(true);
      void qc.invalidateQueries({ queryKey: ['meldingen', 'detail', externalId] });
    },
    onError: (e) => toast.error(t('melden.fout'), { description: formatApiError(e, t('melden.fout')) }),
  });

  async function kies(bestand: File | undefined) {
    const b = bestand ? await bestandNaarBijlage(bestand) : null;
    if (bestand && !b) toast.error(t('melden.gesprek.beeldFout'));
    setBeeld(b);
  }

  if (klaar) return <output className="block text-[13px] text-text-secondary">{t('melden.gesprek.verstuurd')}</output>;

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (tekst.trim() || beeld) stuur.mutate(tekst.trim());
      }}
    >
      {vraag.keuzes && vraag.keuzes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {vraag.keuzes.map((k) => (
            <button key={k} type="button" disabled={stuur.isPending} onClick={() => stuur.mutate(k)} className={KNOP}>
              {k}
            </button>
          ))}
        </div>
      )}
      <label className="block text-[12.5px] text-text-muted">
        {t('melden.gesprek.antwoord')}
        <textarea rows={3} maxLength={4000} value={tekst} onChange={(e) => setTekst(e.target.value)} className={VELD} />
      </label>
      <label className="block text-[12.5px] text-text-muted">
        {t('melden.gesprek.beeld')}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => void kies(e.target.files?.[0])}
          className="mt-1 block text-[12.5px] text-text-secondary"
        />
      </label>
      <button type="submit" disabled={stuur.isPending || (!tekst.trim() && !beeld)} className={KNOP}>
        {t('melden.gesprek.sturen')}
      </button>
    </form>
  );
}
